import { SectionTypeStructureFieldList, WithId } from '@superfunnel/types';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { SectionStructureForm } from './SectionStructureForm';

interface Props<T extends WithId<Record<string, any>>> {
  item: T | undefined;
  field: SectionTypeStructureFieldList | undefined;
  open: boolean;
  onClose: () => void;
  onSave: (item: T) => void | Promise<void>;
}

export function EditStructuredSectionItemModal<
  T extends WithId<Record<string, any>>,
>({ item, field, open, onClose, onSave }: Props<T>) {
  const { show } = useContext(SnackbarContext);

  const [isSaving, setIsSaving] = useState(false);
  const [editableItem, setEditableItem] = useState<WithId<Record<string, any>>>(
    { id: uid(12) },
  );

  useEffect(() => {
    setEditableItem(item ? { ...item } : { id: uid(12) });
    setIsSaving(false);
  }, [open, item]);

  const onSaveButtonClick = useCallback(() => {
    if (!editableItem) {
      show('No item to save');
      return;
    }

    let relevant = true;
    const result = onSave(editableItem as any);

    if (result instanceof Promise) {
      setIsSaving(true);
      result
        .then(() => {
          if (!relevant) return;
          onClose();
        })
        .catch((error) => {
          if (!relevant) return;
          show(error.message);
        })
        .finally(() => {
          if (!relevant) return;
          setIsSaving(false);
        });
    } else {
      onClose();
    }

    return () => {
      relevant = false;
    };
  }, [editableItem, onClose, onSave, show]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: isSaving,
        onClick: () => onClose(),
      },
      {
        title: item ? 'Update' : 'Create',
        type: 'primary',
        loading: isSaving,
        onClick: () => onSaveButtonClick(),
      },
    ],
    [isSaving, item, onClose, onSaveButtonClick],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      actions={actions}
      width="xl"
      title={item ? 'Update item' : 'Create a new item'}
    >
      <div className="py-0">
        {!!field && (
          <SectionStructureForm
            key={editableItem.id}
            content={editableItem}
            fields={field.structure.fields}
            onChange={(value) => {
              console.log('value', value);
              setEditableItem({ ...value });
            }}
          />
        )}
      </div>
    </Modal>
  );
}
